import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useT } from '../i18n/i18n';
import Celebration from './Celebration';
import { count, flips, openMoves, play, startBoard, type Board } from './reversiRules';

export type ReversiOptions = { size: number; first: 1 | 2; hints: boolean; motion: boolean };

export const REVERSI_DEFAULTS: ReversiOptions = { size: 4, first: 1, hints: true, motion: true };

export function ReversiOptionsForm({ value, onChange }: { value: ReversiOptions; onChange: (v: ReversiOptions) => void }) {
  const t = useT();
  return (
    <>
      <div className="field">
        <label htmlFor="rv-size">{t('Board size')}</label>
        <select id="rv-size" value={value.size} onChange={(e) => onChange({ ...value, size: Number(e.target.value) })}>
          <option value={3}>{t('3 by 3 — very short')}</option>
          <option value={4}>{t('4 by 4')}</option>
          <option value={5}>{t('5 by 5')}</option>
        </select>
      </div>
      <div className="field">
        <label htmlFor="rv-first">{t('Who goes first')}</label>
        <select id="rv-first" value={value.first} onChange={(e) => onChange({ ...value, first: Number(e.target.value) as 1 | 2 })}>
          <option value={1}>{t('Dark')}</option>
          <option value={2}>{t('Light')}</option>
        </select>
      </div>
      <label className="check">
        <input type="checkbox" checked={value.hints} onChange={(e) => onChange({ ...value, hints: e.target.checked })} />
        {t('Show where a piece can go')}
      </label>
      <label className="check">
        <input type="checkbox" checked={value.motion} onChange={(e) => onChange({ ...value, motion: e.target.checked })} />
        {t('Moving pieces: pieces drop in and flip over')}
      </label>
    </>
  );
}

type Last = { seq: number; at: number; player: 1 | 2; turned: number[]; before: Board };

const angle = (d: number) => (d === 2 ? 180 : 0);

export default function Reversi({ options }: { options: ReversiOptions }) {
  const t = useT();
  const n = options.size;
  const [board, setBoard] = useState<Board>(() => startBoard(n));
  const [turn, setTurn] = useState<1 | 2>(options.first);
  const [last, setLast] = useState<Last | null>(null);
  const discs = useRef<(HTMLSpanElement | null)[]>([]);
  const { cells: moves, open } = useMemo(() => openMoves(board, n, turn), [board, n, turn]);
  const other = (p: 1 | 2): 1 | 2 => (p === 1 ? 2 : 1);
  const name = (p: 1 | 2) => (p === 1 ? t('Dark') : t('Light'));
  const over = board.every((d) => d !== 0);
  const { dark, light } = count(board);

  useLayoutEffect(() => {
    if (!last || !options.motion) return;
    const r0 = Math.floor(last.at / n), c0 = last.at % n;
    const dist = (i: number) => Math.hypot(Math.floor(i / n) - r0, (i % n) - c0);
    const placed = discs.current[last.at];
    if (placed) {
      const a = angle(last.player);
      placed.animate(
        [{ transform: `translateY(-46px) rotateY(${a}deg) scale(1.25)`, opacity: 0 }, { transform: `translateY(4px) rotateY(${a}deg) scale(0.96)`, opacity: 1, offset: 0.75 }, { transform: `rotateY(${a}deg)`, opacity: 1 }],
        { duration: 380, easing: 'ease-in' },
      );
    }
    for (const i of last.turned) {
      const el = discs.current[i];
      if (!el) continue;
      el.getAnimations().forEach((a) => a.finish());
      const from = angle(last.before[i]);
      el.animate(
        [{ transform: `rotateY(${from}deg) translateZ(0)` }, { transform: `rotateY(${from + 90}deg) translateZ(18px)` }, { transform: `rotateY(${from + 180}deg) translateZ(0)` }],
        { duration: 520, delay: 260 + dist(i) * 110, easing: 'ease-in-out', fill: 'backwards' },
      );
    }
  }, [last, options.motion, n]);

  useLayoutEffect(() => {
    if (!over || !options.motion || dark === light) return;
    const winner = dark > light ? 1 : 2;
    let k = 0;
    board.forEach((d, i) => {
      const el = discs.current[i];
      if (d !== winner || !el) return;
      el.animate(
        [{ transform: `translateY(0) rotateY(${angle(d)}deg)` }, { transform: `translateY(-14px) rotateY(${angle(d)}deg)` }, { transform: `translateY(0) rotateY(${angle(d)}deg)` }],
        { duration: 360, delay: 700 + k++ * 70, easing: 'ease-out' },
      );
    });
  }, [over, options.motion, board, dark, light]);

  function tap(i: number) {
    if (over) return;
    const turned = flips(board, n, i, turn);
    const next = play(board, n, i, turn);
    if (!next) return;
    setLast({ seq: (last?.seq ?? 0) + 1, at: i, player: turn, turned, before: board });
    setBoard(next);
    setTurn(other(turn));
  }

  function again() {
    setBoard(startBoard(n));
    setTurn(options.first);
    setLast(null);
  }

  const result = dark === light ? t('It is a draw, {n} each.', { n: dark }) : t('{name} wins, {a} to {b}.', { name: dark > light ? t('Dark') : t('Light'), a: Math.max(dark, light), b: Math.min(dark, light) });

  return (
    <div className="lz-reversi">
      <p className="lz-status" aria-live="polite">
        {over ? result : t('{name} to play.', { name: name(turn) })}
        <span className="lz-score">{t('Dark {d} · Light {l}', { d: dark, l: light })}</span>
      </p>
      {!over && open && <p className="field-hint" role="status">{t('No piece can be turned yet, so any empty square is allowed.')}</p>}
      <div className="lz-rv-stage">
        {over && <Celebration text={dark === light ? t('A draw — well played!') : t('{name} wins!', { name: dark > light ? t('Dark') : t('Light') })} motion={options.motion} />}
        <div className="lz-rv-board" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }} role="grid" aria-label={t('Board')}>
          {board.map((d, i) => {
            const can = !over && moves.includes(i);
            return (
              <button
                key={i}
                type="button"
                className="lz-rv-cell"
                disabled={!can}
                aria-label={d ? name(d as 1 | 2) : can ? t('Place here') : t('Empty')}
                onClick={() => tap(i)}
              >
                <span className={`lz-rv-tile${can && options.hints && !open ? ' is-move' : ''}`}>
                  {d !== 0 && (
                    <span ref={(el) => { discs.current[i] = el; }} className="lz-disc" style={{ transform: `rotateY(${angle(d)}deg)` }} aria-hidden="true">
                      <span className="lz-face is-dark" />
                      <span className="lz-face is-light" />
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      {over && <div className="toolbar"><button type="button" className="btn" onClick={again}>{t('Play again')}</button></div>}
    </div>
  );
}
